import { useAuth } from '@/stores/AuthContext';
import { Button, Form, Input, Modal, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

interface LoginDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface LoginFormValues {
  email: string;
  code: string;
}

const VERIFICATION_CODE_LENGTH = 6;
const COUNTDOWN_DURATION = 60; // 60 seconds

export const LoginDialog = observer(
  ({ open, onOpenChange }: LoginDialogProps) => {
    const [form] = Form.useForm<LoginFormValues>();
    const [isSending, setIsSending] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const { sendLoginCode, verifyLoginCode } = useAuth();

    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (countdown > 0) {
        timer = setInterval(() => {
          setCountdown((prev) => prev - 1);
        }, 1000);
      }
      return () => {
        if (timer) clearInterval(timer);
      };
    }, [countdown]);

    const handleSendEmail = async (email: string) => {
      if (isSending || countdown > 0) return;

      setIsSending(true);
      try {
        await sendLoginCode(email);
        setCountdown(COUNTDOWN_DURATION);
        message.success({
          content: 'Verification code sent to email',
          key: 'verification-email-sent',
        });
      } catch (error) {
        message.error({
          content: `Failed to send verification code: ${error}`,
          key: 'send-verification-error',
        });
      } finally {
        setIsSending(false);
      }
    };

    const handleVerifyCode = useCallback(
      async (values: LoginFormValues) => {
        if (isLoggingIn) return;

        setIsLoggingIn(true);
        try {
          await verifyLoginCode(values.email, values.code);
          onOpenChange?.(false);
          form.resetFields();
          message.success({
            content: 'You are now logged in',
            key: 'login-success',
          });
        } catch (error) {
          form.setFieldValue('code', ''); // Clear the invalid code
          message.error({
            content: `Failed to verify code: ${error}`,
            key: 'verify-code-error',
          });
        } finally {
          setIsLoggingIn(false);
        }
      },
      [form, isLoggingIn, verifyLoginCode, onOpenChange],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      return () => {
        form.resetFields();
      };
    }, [open]);

    return (
      <>
        <Modal
          title="Login"
          open={open}
          onCancel={() => onOpenChange?.(false)}
          footer={null}
          width={425}
          style={{ top: '30%' }}
          maskClosable={false}
        >
          <div className="pb-4">
            <p className="text-sm text-gray-500 mb-4">
              Sign in to your account
            </p>
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              onFinish={handleVerifyCode}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  placeholder="Enter your email"
                  disabled={isSending || isLoggingIn}
                  type="email"
                />
              </Form.Item>
              <Form.Item
                name="code"
                label="Verification Code"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the verification code',
                  },
                  {
                    len: VERIFICATION_CODE_LENGTH,
                    message: `Code must be ${VERIFICATION_CODE_LENGTH} digits`,
                  },
                ]}
              >
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter verification code"
                    disabled={isSending || isLoggingIn}
                    maxLength={VERIFICATION_CODE_LENGTH}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                  />
                  <Button
                    onClick={() => {
                      const email = form.getFieldValue('email');
                      if (email) {
                        handleSendEmail(email);
                      }
                    }}
                    loading={isSending}
                    disabled={isLoggingIn || countdown > 0}
                  >
                    {countdown > 0 ? `${countdown}s` : 'Send'}
                  </Button>
                </div>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoggingIn}
                  disabled={isSending}
                  className="w-full"
                >
                  Login
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Modal>
      </>
    );
  },
);
